import { useCallback, useEffect, useMemo } from 'react';
import * as PIXI from 'pixi.js';
import { StateManager } from '../managers/StateManager';
import { InteractionState } from '../types';
import { usePixiAppRefs } from './pixi/refs';
import { isValidNodePosition as isValidNodePositionImpl, calculateOptimalInsertIndex as calculateOptimalInsertIndexImpl } from './pixi/nodePlacement';
import { createWireCreationHandlers, createInteractionStateHandler } from './pixi/wireCreation';
import { createWireHighlighter, wireSegmentIds } from './pixi/wireHighlight';
import { createSelectionRenderer } from './pixi/selectionRender';
import { createRenderEntities } from './pixi/entityRenderer';
import { createInitializeCanvas, updateApplication } from './pixi/canvasLifecycle';
import { contentBounds } from '../utils/fitView';
import { installE2EHook } from './pixi/e2eHook';
import { ContextMenuHandlers } from './pixi/types';

export type { ContextMenuHandlers } from './pixi/types';

/**
 * Owns the PIXI application lifecycle for the harness canvas. Creates the shared
 * refs and composes the rendering, wire-creation and lifecycle modules, exposing
 * a stable `initializeCanvas`/`cleanup` pair to the React tree.
 */
export const usePixiApp = (stateManager: StateManager, contextMenuHandlers: ContextMenuHandlers) => {
  const refs = usePixiAppRefs();

  const isValidNodePosition = useCallback(
    (x: number, y: number) => isValidNodePositionImpl(stateManager.getState(), x, y),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stateManager is stable; adding it would recreate the callback needlessly
    []
  );

  const calculateOptimalInsertIndex = useCallback(
    (wire: any, nodeX: number, nodeY: number) => calculateOptimalInsertIndexImpl(stateManager.getState(), wire, nodeX, nodeY),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stateManager is stable; adding it would recreate the callback needlessly
    []
  );

  const wireCreation = useMemo(
    () => createWireCreationHandlers(refs, stateManager, contextMenuHandlers),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs and stateManager are stable for the component's lifetime
    []
  );

  const handleInteractionState = useMemo(
    () => {
      const reactToInteraction = createInteractionStateHandler(refs, stateManager);
      const applyHighlight = createWireHighlighter(refs);
      const applySelection = createSelectionRenderer(refs);
      return (state: InteractionState) => {
        reactToInteraction(state);
        applyHighlight(wireSegmentIds(stateManager, state.highlightedWireId), state.highlightedWireId);
        applySelection(new Set(state.selectedIds ?? []));
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs and stateManager are stable for the component's lifetime
    []
  );

  const renderEntities = useMemo(
    () => createRenderEntities({
      refs,
      stateManager,
      contextMenuHandlers,
      isValidNodePosition,
      calculateOptimalInsertIndex
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dependencies are stable; recreating would drop entity event bindings
    []
  );

  const updateApplicationCallback = useCallback(
    (ticker: PIXI.Ticker) => updateApplication(ticker, refs),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs is stable for the component's lifetime
    []
  );

  const initializeCanvas = useMemo(
    () => createInitializeCanvas({
      refs,
      stateManager,
      contextMenuHandlers,
      renderEntities,
      handleInteractionState,
      handleWireCreationClick: wireCreation.handleWireCreationClick,
      handleWireCreationCancel: wireCreation.handleWireCreationCancel,
      updateApplication: updateApplicationCallback
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dependencies are stable for the component's lifetime
    []
  );

  /** Pans and zooms the canvas so every connector and node fits in view. */
  const fitToView = useCallback(() => {
    const viewportManager = refs.viewportManager.current;
    const app = refs.appManager.current?.getApp();
    if (!viewportManager || !app) {
      return;
    }
    const state = stateManager.getState();
    const bounds = contentBounds(state.connectors, state.nodes, state.wires);
    if (!bounds) {
      return;
    }
    const rect = (app.canvas as HTMLCanvasElement).getBoundingClientRect();
    viewportManager.fitToView(bounds, rect.width, rect.height);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs and stateManager are stable for the component's lifetime
  }, []);

  /** Converts client (page) coordinates to world coordinates, or null if not ready. */
  const screenToWorld = useCallback((clientX: number, clientY: number): { x: number; y: number } | null => {
    const viewportManager = refs.viewportManager.current;
    const app = refs.appManager.current?.getApp();
    if (!viewportManager || !app) {
      return null;
    }
    const rect = (app.canvas as HTMLCanvasElement).getBoundingClientRect();
    return viewportManager.screenToWorld(clientX - rect.left, clientY - rect.top);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs is stable for the component's lifetime
  }, []);

  const cleanup = useCallback(() => {
    refs.mounted.current = false;
    if (refs.cleanupFunction.current) {
      refs.cleanupFunction.current();
      refs.cleanupFunction.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs is stable for the component's lifetime
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  useEffect(() => {
    if (process.env.REACT_APP_E2E === 'true') {
      installE2EHook(refs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs is stable for the component's lifetime
  }, []);

  return {
    initializeCanvas,
    fitToView,
    screenToWorld,
    cleanup
  };
};
